/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Handle, Position } from 'reactflow';
import { GenericCard } from '../../../../../ui-components/Card/GenericCard';
import { GenericTag } from '../../../../../ui-components/Tag/GenericTag';
import { VpcData, TagAttribution } from '../../BgpVisibilityData';
import { formatTagAttribution } from '../../../../helpers/bgpValidation';
import { countDistinctPrefixes } from '../../utils/routeModel';

/**
 * The tag attribution that put a route table / subnet in scope, if any (CTX-129). All of a
 * destination's prefixes were matched by the same rule, so the first one describes it.
 */
const getTagAttribution = (routeTable: any): TagAttribution | null =>
  routeTable?.prefixes?.find((prefix: any) => prefix?.tag_attribution)?.tag_attribution ?? null;

/** Tooltip text listing every matching tag, or null when the destination carries none. */
const tagTooltip = (routeTable: any): string | null => {
  const labels = formatTagAttribution(getTagAttribution(routeTable));
  return labels.length > 0 ? labels.join(' · ') : null;
};

/**
 * What a destination's entry list amounts to, and why the two directions count differently.
 *
 * INBOUND counts **distinct prefixes**. The normalized model expands one entry per candidate
 * path, so a prefix learned from two neighbors appears twice; the raw length reported `32
 * prefixes` on a canvas whose toolbar reported `16` (CTX-322). This now goes through the same
 * `countDistinctPrefixes` the toolbar and the edge badges use.
 *
 * OUTBOUND counts **announcements**, and its labels say so. A subnet's CIDR announced to three
 * neighbors is three announcements, and the subnet modal lists one card per neighbor — deduping
 * by prefix would print `1` above a list of three, which is the very mismatch being removed.
 */
const destinationCount = (routeTable: any, isOutbound: boolean): number =>
  isOutbound ? routeTable.prefixes.length : countDistinctPrefixes(routeTable.prefixes);

/** The VPC-level figure: distinct across the whole VPC inbound, total announcements outbound. */
const vpcTotalCount = (routeTables: any[], isOutbound: boolean): number =>
  isOutbound
    ? routeTables.reduce((sum: number, rt: any) => sum + rt.prefixes.length, 0)
    : countDistinctPrefixes(routeTables.flatMap((rt: any) => rt.prefixes));

interface VpcCardProps {
  data: VpcData;
  onCardClick?: (data: VpcData) => void;
  onRouteTableClick?: (routeTableData: VpcData['routeTables'][0], vpcId: string) => void;
  isOutbound?: boolean;
}

export const VpcCard: React.FC<VpcCardProps> = ({ 
  data, 
  onRouteTableClick, 
  isOutbound = false 
}) => {
  const { vpcId, routeTables } = data;
  
  const headerContent = (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2">
        <h3 className="font-semibold text-semantic-text-primary">{vpcId}</h3>
      </div>
      <GenericTag label="AWS VPC" variant="warning" size="small" />
    </div>
  );

  const bodyContent = (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <GenericTag 
          label={isOutbound ? `Subnets ${routeTables.length}` : `RTs ${routeTables.length}`} 
          variant="neutral" 
          size="small" 
        />
        <GenericTag
          label={isOutbound
            ? `Announced ${vpcTotalCount(routeTables, true)}`
            : `Prefixes ${vpcTotalCount(routeTables, false)}`
          }
          variant="neutral"
          size="small"
        />
        <GenericTag 
          label={`Neighbors ${new Set(routeTables.flatMap((rt: any) => rt.prefixes.map((p: any) => p.neighbor_ip))).size}`} 
          variant="neutral" 
          size="small" 
        />
      </div>
      
      <div className="space-y-2">
        {routeTables.map((rt: any, index: number) => (
          <div 
            key={index} 
            className="flex items-center justify-between p-2 bg-semantic-bg-secondary rounded border border-semantic-border-secondary relative cursor-pointer hover:bg-semantic-bg-tertiary transition-colors"
            style={{ pointerEvents: 'auto', zIndex: 40 }}
            onClick={(e) => {
              e.stopPropagation();
              onRouteTableClick?.(rt, vpcId);
            }}
          >
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-sm text-semantic-text-primary">
                {rt.route_table_id.length > 20
                  ? `${rt.route_table_id.substring(0, 8)}...${rt.route_table_id.substring(rt.route_table_id.length - 8)}`
                  : rt.route_table_id
                }
              </span>
              {/* Tag-discovered destinations are marked here so the provenance is visible on the
                  canvas; the full `name = value` lives in the modal. The row is already tight
                  (id + count + connection handle), so this stays a compact marker. */}
              {tagTooltip(rt) && (
                <span
                  className="text-[10px] leading-none px-1 py-0.5 rounded border border-semantic-border-secondary text-semantic-text-secondary"
                  title={`Tags: ${tagTooltip(rt)}`}
                >
                  tag
                </span>
              )}
            </div>
            <GenericTag
              label={isOutbound
                ? `${destinationCount(rt, true)} announced`
                : `${destinationCount(rt, false)} prefixes`
              }
              variant="neutral"
              size="small"
            />
            <Handle
              type={isOutbound ? "source" : "target"}
              position={isOutbound ? Position.Left : Position.Left}
              id={`handle-rt-${rt.route_table_id}`}
              className="rtHandle"
              style={{ 
                pointerEvents: 'auto', 
                zIndex: 10, 
                position: 'absolute', 
                left: -6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 6,
                height: 6,
                borderRadius: 9999,
                background: '#e8f0ff',
                opacity: 0.35,
                border: '1px solid #7aa7ff',
                boxShadow: 'none'
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div 
      className="cursor-pointer"
      style={{ pointerEvents: 'auto' }}
    >
      <GenericCard
        variant="primary"
        shadow="sm"
        isCollapsible={false}
        headerContent={headerContent}
        bodyContent={bodyContent}
        className="min-w-[300px]"
      />
    </div>
  );
};

